import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import './Solutions.css';

interface SolutionDetail {
  title: string;
  overview: string;
  introduction: {
    title: string;
    description: string;
  }[];
}

const Solutions: React.FC = () => {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSolution, setSelectedSolution] = useState<SolutionDetail | null>(null);
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const solutionDetails: { [key: string]: SolutionDetail } = {
    'Smart Office': {
      title: 'Smart Office',
      overview: 'Aqara smart office solution provides enterprises with a complete solution for office buildings. Based on the Aqara IoT system and smart office SaaS applications, it provides digital services for enterprise operations. With IoT devices, it can help manage and control lighting, HVAC, shading, water and electricity security, providing environment monitoring, office energy consumption management, security management and real-time message push services, etc. It also provides customizable scenes for different areas to provide a comfortable, safe and healthy office environment while saving energy, and improving the work efficiency of employees.\n\nAdditionally, with Aqara\'s capabilities in big data and AI technology, it can implement space management, hierarchical partition access control, AI smart scenes, AI entrance/exit control, AI attendance, AI pedestrian trajectory monitoring, and meeting room reservation management.\n\nWith our powerful data integration and automated processing capabilities, enterprises can achieve data-driven energy conservation, significantly cut energy usage and operational expenses, save energy, and minimize carbon emissions.',
      introduction: [
        {
          title: 'Smart access control',
          description: 'It makes access control and attendance more convenient, ensuring safety and efficiency.\n• Fast Pass\n• Automatic Clock-in/out\n• Suspicious Person Detection\n• Risk Warning\n• Smart Welcome\n• Unmanned Security Guard'
        },
        {
          title: 'Smart meeting',
          description: 'Never delay any important meeting.\n• Check-in with Face Recognition\n• Timeout Warning\n• Resource Release\n• Participation Reminder\n• Projection Scene\n• Schedule Display'
        },
        {
          title: 'Smart workstation',
          description: 'Provides better office experience and improves work efficiency.\n• Temperature Adjustment\n• Smart Reminder\n• Sedentary Warning\n• Office Mode\n• Lunch Break Mode\n• Off Work Mode'
        },
        {
          title: 'Smart administration',
          description: 'It helps with data visualization, smart decision-making, energy saving and efficiency enhancement, and security protection.\n• Attendance Analysis\n• Energy Consumption Control\n• Auxiliary Decision-Making\n• Suspicious Person Warning\n• Fire Warning\n• One-button Arming'
        }
      ]
    },
    'Smart Community': {
      title: 'Smart Community',
      overview: 'The Aqara smart community solution can carry out functions such as unobtrusive access control, holistic smart home automation, smart monitoring, and linkage between home and community security, making life in the community more convenient, management more efficient, and service more thoughtful.',
      introduction: [
        {
          title: 'Unobtrusive access control',
          description: 'Users can enter the community and home with face recognition at the community entrance, elevator, and door lock at home, making access more convenient.'
        },
        {
          title: 'Smart monitoring',
          description: 'The property management center can use AI to analyze the surveillance footage for features like face control, trajectory query, behavior analysis, smart alert, etc. to ensure the safety of community members 24/7 and cut down on staff costs associated with property security and maintenance.'
        },
        {
          title: 'Smart home',
          description: 'With abundant scene modes, automations and multiple control methods, it can utilize smart control of lighting, curtains, air conditioners and other appliances in the home, and protect family and property safety through smart security solutions.\n\nWith the apartment owner\'s authorization, water, electricity and gas can be connected to the community which protects the security and safety of the residents in the community.'
        }
      ]
    },
    'Smart Elderly Care': {
      title: 'Smart Elderly Care',
      overview: 'With Zigbee technology, the Aqara IoT Platform, big data and AI technology, Aqara smart elderly care solution helps support and manage the daily life of the elderly including health and safety, life convenience, entertainment and leisure and other aspects.\n\nA friendly, autonomous, and customized interaction for the elderly is made possible by the automatic monitoring, early warning, and even active management of information pertaining to seniors. This technology also supports the provision of home-based elderly care services to achieve socially intelligent elderly care services in partnership with communities and elderly care institutions.',
      introduction: [
        {
          title: 'Create a comfortable and healthy living environment',
          description: 'Improving the air, water, light and noise living environment of the elderly.'
        },
        {
          title: 'Make daily life more convenient',
          description: 'Assisting the elderly in daily life care such as transportation, bathing, etc.'
        },
        {
          title: '24hr care and AI security',
          description: 'Home security is integrated with 24 hour home health and friendly reminders to offer a guarantee of safety and comfort.'
        },
        {
          title: 'Help you enjoy the care and companionship of children all the time',
          description: 'The time and spatial distance between the elderly and their children is reduced by digital management and remote connectivity.'
        },
        {
          title: 'Customizable based on your habits',
          description: 'The solution can enrich the senior life scenario experience, inheriting seniors\' frugal habits to help achieve carbon neutrality.'
        },
        {
          title: 'Enjoy care of the whole society at home',
          description: 'The open system enables multi-platform integration among home, community and medical institutions, and the whole society can work together to provide services for the elderly.'
        }
      ]
    },
    'Smart Hotel': {
      title: 'Smart Hotel',
      overview: 'Aqara smart hotel solution is suitable for hotels, homestays, etc. With the Aqara smart hotel system and smart devices, we provide AIoT integrated hotel smart services for different types of hotels, creating an immersive and comprehensive smart space experience for hotel guests, and providing smart service for hotel guests throughout the stay, giving guests a more convenient check-in experience while improving the hotel service quality and reducing hotel operating costs.',
      introduction: [
        {
          title: 'Functions',
          description: '(1) Intelligentization of the entire check-in process: Online reservations and payment, smart check-ins, elevator control with facial recognition, voice customer control/demand, robot deliveries, one-click check-out and invoicing.\n\n(2) Smart room control: welcome speech, music control, movie control, lighting control, temperature control, curtain control, smart bathroom, smart door locking, sleep monitoring, scene customization, voice customer demand, customer control mini program, etc.'
        },
        {
          title: 'Values',
          description: '(1) Improves hotel image: It helps upgrade the hotel service and build up a brand new image for hotels.\n\n(2) Low retrofit cost: The wireless technology saves the cost of physical wires.\n\n(3) Improve user experience: Automatically adjust indoor lighting, curtains, temperature, etc., so that guests can always enjoy a comfortable environment.\n\n(4) Reduce energy consumption: On average, lighting and central air-conditioning consume 70% of the energy in a project, and the smart hotel solution can help reduce energy usage by 30%.\n\n(5) More reliable and stable solution: With the software and hardware integrated solution, it provides users with an IoT platform that is more powerful, cost-effective and stable.\n\n(6) Improve operation efficiency: It helps improve the efficiency through automatic control, centralized control, and simplified operation; functions such as remote and background management improve management efficiency and reduce labor costs.'
        }
      ]
    }
  };

  const handleLearnMoreClick = (solutionTitle: string) => {
    setSelectedSolution(solutionDetails[solutionTitle]);
    setIsModalOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedSolution(null);
    document.body.style.overflow = 'auto';
  };

  const handleNavigateToProducts = () => {
    navigate('/', { state: { scrollTo: 'products' } });
  };

  const solutions = [
    { id: 'smart-office', title: 'Smart Office', image: 'https://www.aqara.com/wp-content/uploads/2023/03/%E5%9B%BE%E7%89%87-5-1.png', description: 'CLEVR transforms office environments with intelligent automation, energy management, and seamless control of lighting, climate, security, and workplace productivity systems.' },
    { id: 'smart-community', title: 'Smart Community', image: 'https://www.aqara.com/wp-content/uploads/2023/03/Smart-Community.png.webp', description: 'CLEVR can help community residents, facility managers, or security services perform functions, such as multimedia access control, remote control, lighting, smart panels, and more.' },
    { id: 'smart-elderly-care', title: 'Smart Elderly Care', image: 'https://www.aqara.com/wp-content/uploads/2023/03/Smart-Elderly-Care.png.webp', description: 'CLEVR can provide homes for the elderly big data and AI technology; higher price efficient care solution helps our medical facilities, to offer safer care.' },
    { id: 'smart-hotel', title: 'Smart Hotel', image: 'https://www.aqara.com/wp-content/uploads/2023/03/%E5%9B%BE%E7%89%87-6-1.png', description: 'Aqara smart hotel solution is suitable for hotels, homestays, etc. With the Aqara smart hotel system and smart devices, we provide AIoT integrated hotel smart services...' }
  ];

  const handlePrevSlide = () => {
    setCurrentSlide((prev) => Math.max(0, prev - 1));
  };

  const handleNextSlide = () => {
    setCurrentSlide((prev) => Math.min(solutions.length - 3, prev + 1));
  };

  return (
    <>
      <Header />
      <div className="solutions-page">
        {/* Hero Section */}
        <section className="solutions-hero" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1497366216548-37526070297c?q=95&w=2000&auto=format&fit=crop)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
          <div className="solutions-hero-overlay"></div>
          <div className="solutions-hero-content">
            <h1 className="solutions-hero-title">
              Shaping the Future of Connected Living with Smart Home Intelligence
            </h1>
            <button className="solutions-hero-btn">
              <span className="solutions-hero-icon">▶</span> Consult
            </button>
          </div>
        </section>

        {/* What is Smart Home Intelligence Section */}
        <section className="solutions-about">
          <div className="solutions-container">
            <h2 className="solutions-section-title">What is Smart Home Intelligence</h2>
            <div className="solutions-about-content">
              <p className="solutions-about-text">
                Smart home intelligence is the next evolution of smart home technology, blending beyond traditional control systems that rely on active user input. 
                Smart intelligence enables smarter, more intuitive automation that can understand their physical environment and user behaviors, adapt, and anticipate the occupants' needs, and act.
              </p>
              <p className="solutions-about-text">
                By consolidating multi-dimensional sensors and advanced AI, smart intelligence allows your home to perceive the space around it, learn from your preferences, 
                and predict, and deeply apply alongside smart home habits. Now that it delivers automated response through continuous learning, delivering a more intuitive, proactive, 
                and seamless experience.
              </p>
              <p className="solutions-about-text">
                This shift – from business controlling aspects to systems naturally adapting to business - fundamentally redefines how environments whether residential, commercial, or 
                public. By removing the need for constant manual monitoring and interaction, smart intelligence transforms the very nature of living with the property.
              </p>
            </div>
          </div>
        </section>

        {/* Comprehensive Products Section */}
        <section className="solutions-products">
          <div className="solutions-products-header">
            <h2 className="solutions-section-title-white">
              Comprehensive Products<br />and Software Solutions
            </h2>
          </div>
          <div className="solutions-container">
            <div className="solutions-cards">
              <div className="solutions-card">
                <div className="solutions-card-image">
                  <img src="https://www.aqara.com/wp-content/uploads/2023/03/aqara-products.png" alt="CLEVR Products" />
                </div>
                <h3 className="solutions-card-title">CLEVR Products</h3>
                <p className="solutions-card-text">
                  CLEVR is a leading smart home solution provider with 1,200+ powerful products covering 50 device categories. 
                  Our products work seamlessly together, provide smart home installation solutions, and make your home smarter.
                </p>
                <button className="solutions-card-btn" onClick={handleNavigateToProducts}>Learn More</button>
              </div>

              <div className="solutions-card">
                <div className="solutions-card-image">
                  <img src="https://www.aqara.com/wp-content/uploads/2023/03/aqara-studio.png" alt="CLEVR Studio" />
                </div>
                <h3 className="solutions-card-title">CLEVR Studio</h3>
                <p className="solutions-card-text">
                  CLEVR Studio is the designer, customizable for any platform that seamlessly integrates project design, programming, 
                  debugging, remote management, and installation in an easy-to-use all-in-one intuitive interface.
                </p>
                <button className="solutions-card-btn" onClick={() => navigate('/clevr-studio')}>Learn More</button>
              </div>

              <div className="solutions-card">
                <div className="solutions-card-image">
                  <img src="https://www.aqara.com/wp-content/uploads/2023/03/developer-platform.png" alt="Developer Platform" />
                </div>
                <h3 className="solutions-card-title">CLEVR Developer Platform</h3>
                <p className="solutions-card-text">
                  A one-stop Tool platform that provides customers with answers for rapid product development. At CLEVR based 
                  Developer Platform help companies build our extensive range of platforms & protocols.
                </p>
                <button className="solutions-card-btn">Learn More</button>
              </div>
            </div>
          </div>
        </section>

        {/* Leading Industrial Solutions Section */}
        <section className="solutions-industrial">
          <div className="solutions-container">
            <h2 className="solutions-section-title">Leading Industrial Solutions</h2>
            <div className="solutions-carousel-container">
              <button 
                className="carousel-arrow carousel-arrow-left" 
                onClick={handlePrevSlide}
                style={{ visibility: currentSlide === 0 ? 'hidden' : 'visible' }}
              >
                ‹
              </button>
              <div className="solutions-cards">
                {solutions.slice(currentSlide, currentSlide + 3).map((solution) => (
                  <div key={solution.id} className="solutions-card">
                    <div className="solutions-card-image-industrial">
                      <img src={solution.image} alt={solution.title} />
                    </div>
                    <h3 className="solutions-card-title">{solution.title}</h3>
                    <p className="solutions-card-text">
                      {solution.description}
                    </p>
                    <button className="solutions-card-btn" onClick={() => handleLearnMoreClick(solution.title)}>Learn More</button>
                  </div>
                ))}
              </div>
              <button 
                className="carousel-arrow carousel-arrow-right" 
                onClick={handleNextSlide}
                style={{ visibility: currentSlide >= solutions.length - 3 ? 'hidden' : 'visible' }}
              >
                ›
              </button>
            </div>
          </div>
        </section>

        {/* Modal for Solution Details */}
        {isModalOpen && selectedSolution && (
          <div className="solution-modal-overlay" onClick={handleCloseModal}>
            <div className="solution-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="solution-modal-close" onClick={handleCloseModal}>×</button>
              
              <h2 className="solution-modal-title">{selectedSolution.title}</h2>
              
              <div className="solution-modal-section">
                <h3 className="solution-modal-section-title">Overview</h3>
                <p className="solution-modal-text">{selectedSolution.overview}</p>
              </div>

              <div className="solution-modal-section">
                <h3 className="solution-modal-section-title">Introduction</h3>
                <div className="solution-modal-intro-list">
                  {selectedSolution.introduction.map((item, index) => (
                    <div key={index} className="solution-modal-intro-item">
                      <h4 className="solution-modal-intro-title">{item.title}</h4>
                      <p className="solution-modal-intro-text">{item.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default Solutions;
